'use client'

import { type Messages, setupI18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import {
	createContext,
	type PropsWithChildren,
	useContext,
	useMemo,
} from 'react'

import type { Locale } from '~/i18n/routing'

type LocaleContextValue = {
	locale: Locale
}

const LocaleContext = createContext<LocaleContextValue>({
	locale: 'fr',
})

export const useLocaleContext = () => useContext(LocaleContext)

export const LocaleProvider = ({
	children,
	locale,
	messages,
}: PropsWithChildren<{
	locale: Locale
	messages: Messages
}>) => {
	const value = useMemo(() => ({ locale }), [locale])
	const i18n = useMemo(
		() =>
		setupI18n({ locale, messages: { [locale]: messages } }),
		[locale, messages],
	)

	// Keep dayjs in sync during render (SSR + first client paint) so formatted
	// times match across hydration. AppShell's useEffect is too late.
	dateTimeUtils.locale(locale)

	return (
		<LocaleContext.Provider value={value}>
			<I18nProvider i18n={i18n}>{children}</I18nProvider>
		</LocaleContext.Provider>
	)
}
